import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {
    DashboardXlsExportingSettings,
    IDashboardPdfTemplateSettings,
    IDashboardView,
    IDashboardWidgetView,
} from "@main/reporting.v3/dashboard/dashboard-models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import {
    IQueryPageRequest,
    PdfExportSettings,
    PdfExportSource,
    WidgetOutputMode
} from "@main/reporting.v3/core/reporting-models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { DashboardPdfExportingService } from "@main/reporting.v3/dashboard/api/dashboard-pdf-exporting.service";
import { DashboardXlsExportingService } from "@main/reporting.v3/dashboard/api/dashboard-xls-exporting.service";
import { IEmittedVariable, IQueryVariables, IStaticVariable } from "@main/reporting.v3/core/variables/variable-models";
import { DashboardQueryResolverService } from "@main/reporting.v3/dashboard/services/dashboard-query-resolver.service";
import { DashboardAuxiliaryService } from "@main/reporting.v3/dashboard/api/dashboard-auxiliary.service";
import { dashboardQueryResolverProvider } from "../models/dashboard-query-resolver-provider.model";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { VariableHubService } from "@main/reporting.v3/core/variables/variable-hub.service";
import { IVariableReceiver } from "@main/reporting.v3/core/variables/variable-receiver";
import { DashboardService } from "@main/reporting.v3/dashboard/api/dashboard.service";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { IXlsExportable } from "@main/reporting.v3/core/exporting/xls-exportable";
import { BehaviorSubject } from "rxjs";
import { v4 as uuidv4 } from "uuid";


@Component({
    selector: "reporting-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    providers: [
        DashboardQueryResolverService,
        dashboardQueryResolverProvider,
    ]
})
export class ReportingDashboardComponent implements IVariableReceiver, IPdfExportable, IXlsExportable, OnInit, OnDestroy {
    // Variable handling settings.
    public readonly variableReceiverId: string = uuidv4();

    // CSS injection settings.
    public readonly cssInjectionScopeId: string = uuidv4();

    // Pdf view settings.
    public pdfViewVisible: boolean = true;

    // Xls view settings.
    public xlsViewVisible: boolean = true;

    // Exporting settings.
    public pdfExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public xlsExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Component fields.
    public widgetComponents: IWidgetComponent[][] = [];
    public dashboardGrid: IDashboardWidgetView[][] = [];

    private variables: IEmittedVariable[] = [];

    private defaultHeaderHtmlCode: string;
    private defaultHeaderCssStyles: string;
    private defaultFooterHtmlCode: string;
    private defaultFooterCssStyles: string;
    private defaultDashboardStyles: string;

    private _dashboardId: string;
    private _dashboardCode: string;
    private _dashboardView: IDashboardView;
    private _renderHeaderAndFooter: boolean = true;

    // Expands of collapses the content.
    @Input() collapsed: boolean = false;

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Format of the PDF exporting file.
    @Input() pdfExportingFormat: string = null;

    // Determines which html code should be used for rendering: web view of pdf exporting view.
    @Input() output: WidgetOutputMode = "web";

    // Set of fixed-value variables to pass to inner widgets.
    @Input() staticVariables: IStaticVariable[] = [];

    // NHS Settings:
    @Input() showIm1HtmlWidget: boolean = false;
    @Input() loadingIm1Info: boolean = false;
    @Input() im1MessageHtmlCode: string;
    @Input() im1MessageCssRules: string;

    @Output() readonly im1DataRefresh: EventEmitter<void> = new EventEmitter();

    constructor(private dashboardPdfExportingService: DashboardPdfExportingService,
                private dashboardQueryResolverService: DashboardQueryResolverService,
                private dashboardXlsExportingService: DashboardXlsExportingService,
                private dashboardAuxiliaryService: DashboardAuxiliaryService,
                private htmlRenderingService: HtmlRenderingService,
                private variableHubService: VariableHubService,
                private dashboardService: DashboardService,
                private elementRef: ElementRef,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    // Determines if dashboard is able to receive variables.
    @Input() set variableReceiver(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableReceiver(this);
        else this.variableHubService.unregisterVariableReceiver(this);
    }

    // Shows or hides header and footer of PDF view.
    @Input() set renderHeaderAndFooter(value: boolean) {
        this._renderHeaderAndFooter = value;
    }

    get renderHeaderAndFooter(): boolean {
        return this._renderHeaderAndFooter && this.output === "PDF";
    }

    @Input() set dashboardView(value: IDashboardView) {
        if (!value) return;

        // TODO: Temporary approach until we implement a dashboard store.
        this.dashboardQueryResolverService.dashboardId = value.id;

        this._dashboardView = value;
        this._dashboardId = value.id;

        this.refreshDashboard();
        this.changeDetectorRef.detectChanges();
    };

    get dashboardView(): IDashboardView {
        return this._dashboardView;
    }

    @Input() set dashboardId(value: string) {
        if (!value) return;

        // TODO: Temporary approach until we implement a dashboard store.
        this.dashboardQueryResolverService.dashboardId = value;

        this._dashboardId = value;

        this.refreshDashboardById().then();
    }

    get dashboardId(): string {
        return this._dashboardId;
    }

    @Input() set dashboardCode(value: string) {
        if (!value) return;
        this._dashboardCode = value;
        this.refreshDashboardByCode().then();
    }

    get dashboardCode(): string {
        return this._dashboardCode;
    }

    get dashboardName(): string {
        return this.dashboardView?.name;
    }

    get dashboardStyles(): string {
        return this.dashboardView?.style ?? this.defaultDashboardStyles;
    }

    get dashboardContainerClass(): string {
        return this.dashboardAuxiliaryService.dashboardContainerClass;
    }

    get dashboardContainerNgClass(): object {
        return {
            "collapsed": this.collapsed,
            "pdf-view": this.output === "PDF",
            "border-noround-top": this.renderHeaderAndFooter,
            "border-noround-bottom": this.renderHeaderAndFooter
        };
    }

    get dashboardHeaderClass(): string {
        return this.dashboardAuxiliaryService.dashboardHeaderClass;
    }

    get dashboardHeaderHtmlCode(): string {
        // This is to avoid the loss of performance by html-renderer loading all the content in the dashboard header in vain,
        // when it is not visible. This is necessary because the html-renderer needs to be renderer anyway even
        // when it is not visible, for PDF exporting algorithm to be able to get its component styles.
        return this.renderHeaderAndFooter ? this.dashboardView?.pdfHeaderHtmlCode ?? this.defaultHeaderHtmlCode : "";
    }

    get dashboardHeaderCssStyles(): string {
        // This is to avoid the loss of performance by html-renderer loading all the content in the dashboard header in vain,
        // when it is not visible. This is necessary because the html-renderer needs to be renderer anyway even
        // when it is not visible, for PDF exporting algorithm to be able to get its component styles.
        return this.renderHeaderAndFooter ? this.dashboardView?.pdfHeaderCssStyle ?? this.defaultHeaderCssStyles : "";
    }

    get dashboardHeaderContainerClass(): string {
        return this.renderHeaderAndFooter ? "block" : "hidden";
    }

    get dashboardFooterClass(): string {
        return this.dashboardAuxiliaryService.dashboardFooterClass;
    }

    get dashboardFooterHtmlCode(): string {
        // This is to avoid the loss of performance by html-renderer loading all the content in the dashboard footer in vain,
        // when it is not visible. This is necessary because the html-renderer needs to be renderer anyway even
        // when it is not visible, for PDF exporting algorithm to be able to get its component styles.
        return this.renderHeaderAndFooter ? this.dashboardView?.pdfFooterHtmlCode ?? this.defaultFooterHtmlCode : "";
    }

    get dashboardFooterCssStyles(): string {
        // This is to avoid the loss of performance by html-renderer loading all the content in the dashboard footer in vain,
        // when it is not visible. This is necessary because the html-renderer needs to be renderer anyway even
        // when it is not visible, for PDF exporting algorithm to be able to get its component styles.
        return this.renderHeaderAndFooter ? this.dashboardView?.pdfFooterCssStyle ?? this.defaultFooterCssStyles : "";
    }

    get dashboardFooterContainerClass(): string {
        return this.renderHeaderAndFooter ? "block" : "hidden";
    }

    get widgetGridContainerClass(): string {
        return this.dashboardAuxiliaryService.getWidgetGridContainerClass(this.dashboardView?.layout);
    }

    get widgetRowClass(): string {
        return this.dashboardAuxiliaryService.widgetRowClass;
    }


    public ngOnInit(): void {
        this.defaultDashboardStyles = this.dashboardAuxiliaryService.getDashboardDefaultStyles();
        this.defaultFooterHtmlCode = this.dashboardAuxiliaryService.getDashboardFooterDefaultHtml();
        this.defaultHeaderCssStyles = this.dashboardAuxiliaryService.getDashboardHeaderDefaultStyles();
        this.defaultHeaderHtmlCode = this.dashboardAuxiliaryService.getDashboardHeaderDefaultHtml();
        this.defaultFooterCssStyles = this.dashboardAuxiliaryService.getDashboardFooterDefaultStyles();
    }

    public ngOnDestroy(): void {
        this.variableHubService.unregisterVariableReceiver(this)
    }


    // Variable handling methods.
    public receiveEmittedVariables(variables: IEmittedVariable[]): void {
        // Save current state of variables.
        this.variables = variables;
    }

    // Refreshing methods.
    private async refreshDashboardById(): Promise<void> {
        if (!this._dashboardId) return;
        this.dashboardView = await this.dashboardService.getView(this._dashboardId);
    }

    private async refreshDashboardByCode(): Promise<void> {
        if (!this._dashboardCode) return;
        this.dashboardView = await this.dashboardService.getViewByCode(this._dashboardCode);
    }

    private refreshDashboard(): void {
        this.refreshDashboardStyles();
        this.refreshDashboardGrid();
    }

    private refreshDashboardGrid(): void {
        // Get upperbound of the amount of rows.
        const rows: number = !!this.dashboardView?.widgets?.length ?
            Math.max(...this.dashboardView.widgets.map(widget => widget.rowIndex)) + 1 : 0;

        if (!rows) return;

        // Get a dashboard grid row.
        const getDashboardGridRow = (index: number): IDashboardWidgetView[] => this.dashboardView.widgets
            .filter(widget => widget?.rowIndex === index)
            .sort((first, second) => first.columnIndex - second.columnIndex)

        // Declare a dashboard with that amount of rows.
        this.dashboardGrid = Array(rows + 1).fill(null)
            // Get dashboard widgets corresponding to this row.
            .map((_, i) => getDashboardGridRow(i))
            // Remove empty rows from the dashboard grid.
            .filter(row => row.length > 0);

        // Declare widget components grid with same dimensions as dashboard.
        this.widgetComponents = Array(this.dashboardGrid.length).fill(null)
            .map((_, i) => Array(this.dashboardGrid[i].length))
    }

    private refreshDashboardStyles(): void {
        this.htmlRenderingService.insertScopedStyles(this.cssInjectionScopeId, this.elementRef.nativeElement, this.dashboardStyles);
    }

    protected refreshWidget(component: IWidgetComponent, row: number, column: number): void {
        if (this.output !== component.output) return;
        this.widgetComponents[row][column] = component;
    }

    // Styling methods.
    protected separatorClass(row: number, column?: number): string {
        return this.dashboardAuxiliaryService.getSeparatorClass(this.widgetComponents, this.output, row, column);
    }

    protected widgetContainerClass(widget: IDashboardWidgetView): string {
        return this.dashboardAuxiliaryService.getWidgetContainerClass(widget, this.widgetComponents, this.output);
    }

    protected widgetContainerStyle(widget: IDashboardWidgetView): string {
        return this.dashboardAuxiliaryService.getWidgetContainerStyle(widget,
            this.widgetComponents, this.output, this.dashboardView?.widgetsMargin);
    }

    // Auxiliary methods.
    private getDashboardVariables(): IEmittedVariable[] {
        const staticVariables: IEmittedVariable[] = this.staticVariables?.map(variable =>
            <IEmittedVariable>{ ...variable, empty: false, behaviorOnEmpty: "populate" }) ?? [];

        return staticVariables.concat(this.variables ?? []);
    }

    private getExportingQueryPageRequest(): IQueryPageRequest {
        const queryVariables: IQueryVariables = { variables: this.getDashboardVariables() };
        return { gridSettings: { take: 100_000 }, queryVariables: queryVariables };
    }

    private getPdfVisibleWidgetSourceIds(): string[] {
        return this.widgetComponents.flatMap(row => row
            .filter(widget => (widget as unknown as IPdfExportable)?.pdfViewVisible))
            .map(widget => widget.widgetSourceId);
    }

    private getXlsVisibleWidgetSourceIds(): string[] {
        return this.widgetComponents.flatMap(row => row
            .filter(widget => (widget as unknown as IXlsExportable)?.xlsViewVisible))
            .map(widget => widget.widgetSourceId);
    }

    // PDF exporting methods.
    private getDashboardPdfTemplateSettings(): IDashboardPdfTemplateSettings {
        return {
            format: this.pdfExportingFormat,
            cssStyle: this.dashboardView?.style,
            template: this.elementRef?.nativeElement?.innerHTML,

            widgetComponents: this.widgetComponents,

            headerHtmlCode: this.dashboardView?.pdfHeaderHtmlCode,
            headerCssStyle: this.dashboardView?.pdfHeaderCssStyle,
            footerHtmlCode: this.dashboardView?.pdfFooterHtmlCode,
            footerCssStyle: this.dashboardView?.pdfFooterCssStyle,
        };
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        const settings: IDashboardPdfTemplateSettings = this.getDashboardPdfTemplateSettings();
        return await this.dashboardPdfExportingService.getPdfConfiguration(settings);
    }

    public async generatePdf(): Promise<void> {
        if (this.pdfExportingLoading.value) return;

        try {
            this.pdfExportingLoading.next(true);

            const visibleWidgets: string[] = this.getPdfVisibleWidgetSourceIds();
            const configuration: JsReportConfiguration = await this.getPdfConfiguration();
            const queryPageRequest: IQueryPageRequest = this.getExportingQueryPageRequest();

            const settings: PdfExportSettings = {
                source: PdfExportSource.Dashboard,
                queryPageRequest: queryPageRequest,
                allowedWidgets: visibleWidgets,
                configuration: configuration,
                fileName: this.dashboardName
            };

            await this.dashboardPdfExportingService.requestPdfExport(this.dashboardId, settings);

        } finally {
            this.pdfExportingLoading.next(false);
        }
    }

    // XLS exporting methods.
    public async generateXls(): Promise<void> {
        if (this.xlsExportingLoading.value) return;

        try {
            this.xlsExportingLoading.next(true);

            const visibleWidgets: string[] = this.getXlsVisibleWidgetSourceIds();
            const queryPageRequest: IQueryPageRequest = this.getExportingQueryPageRequest();

            const settings: DashboardXlsExportingSettings = {
                queryPageRequest: queryPageRequest,
                allowedWidgets: visibleWidgets
            };

            await this.dashboardXlsExportingService.generateXls(this.dashboardId, this.dashboardView?.name, settings);

        } finally {
            this.xlsExportingLoading.next(false);
        }
    }
}